import React, { useState, useEffect } from 'react';
import { API } from '../../../../common/Constants';
import useFetch from '../../../../hooks/useFetch';
import { getAPIHeaders } from '../../../../common/Utils';
import CustomModal from '../../../../core/common/customModal';
import CustomToast from '../../../../core/common/customToast';
import CommonSelectV2 from '../../../../core/common/commonSelectV2';

interface Props {
  user: any;
  onSuccess: () => void;
  onClose: () => void;
}

const AddSubscriptionModal: React.FC<Props> = ({ user, onSuccess, onClose }) => {
  const { isLoading, error, callFetch } = useFetch();
  const [availablePlans, setAvailablePlans] = useState<any[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<any>(null);
  const [planOptions, setPlanOptions] = useState<any[]>([]);
  const [toastSettings, setToastSettings] = useState<{
    show: boolean;
    message: string;
    heading: string;
    type: "ERROR" | "SUCCESS" | "INFO";
  }>({
    show: false,
    message: "",
    heading: "",
    type: "INFO",
  });

  useEffect(() => {
    fetchAvailablePlans();
  }, []);

  useEffect(() => {
    if (error) {
      setToastSettings({
        show: true,
        message: error?.response?.data || "Something went wrong please try again later",
        heading: "Error",
        type: "ERROR",
      });
    }
  }, [error]);

  const fetchAvailablePlans = () => {
    callFetch(
      `${API.SUBSCRIPTION_PLAN}/details?includeCourses=true`,
      'GET',
      getAPIHeaders(),
      null,
      null
    ).then((response) => {
      // Filter out plans that the user already has
      const userSubscriptionIds = user.subscriptions.map((sub: any) => sub.subscriptionId);
      const availablePlans = response.filter((plan: any) => 
        plan.planIsActive && !userSubscriptionIds.includes(plan.subscriptionPlanId)
      );
      setAvailablePlans(availablePlans || []);
      
      // Convert to options format for CommonSelectV2
      const options = availablePlans.map((plan: any) => ({
        value: plan?.subscriptionPlanId?.toString(),
        label: `${plan?.subscriptionPlanName} - ${plan?.pricingInfo?.currencyCode} ${plan?.pricingInfo?.price}`,
        data: plan
      }));
      setPlanOptions(options);
    }).catch((err) => {
      console.error('Error fetching available plans:', err);
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedPlan) {
      setToastSettings({
        show: true,
        message: "Please select a subscription plan",
        heading: "Validation Error",
        type: "ERROR",
      });
      return;
    }

    try {
      const endpoint = API.USER_SUBSCRIPTION_ENROLL
        .replace(':subscriptionPlanId', selectedPlan.subscriptionPlanId.toString())
        .replace(':userId', user.userAccountId.toString());

      await callFetch(endpoint, 'POST', getAPIHeaders(), null, null);
      
      setToastSettings({
        show: true,
        message: `Successfully enrolled ${user.userFullName} to ${selectedPlan.subscriptionPlanName}`,
        heading: "Success",
        type: "SUCCESS",
      });

      // Close modal and refresh data after a short delay
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);

    } catch (err: any) {
      setToastSettings({
        show: true,
        message: err?.response?.data || "Failed to enroll user to subscription",
        heading: "Error",
        type: "ERROR",
      });
    }
  };

  const handlePlanChange = (value: string) => {
    if (value) {
      const plan = availablePlans?.find(p => p?.subscriptionPlanId?.toString() === value);
      setSelectedPlan(plan || null);
    } else {
      setSelectedPlan(null);
    }
  };

  const getPlanTypeBadgeClass = (type: string) => {
    switch (type) {
      case 'ENTERPRISE':
        return 'bg-danger';
      case 'PREMIUM':
        return 'bg-success';
      case 'BASIC':
        return 'bg-info';
      default:
        return 'bg-secondary';
    }
  };

  return (
    <>
      <CustomToast
        show={toastSettings?.show}
        message={toastSettings?.message}
        heading={toastSettings?.heading}
        type={toastSettings?.type}
        onClose={() => {
          setToastSettings((prev) => ({
            ...prev,
            show: false,
          }));
        }}
      />

      <CustomModal
        show={true}
        title={`Add Subscription for ${user.userFullName}`}
        onClose={onClose}
        size="lg"
        bodyContent={
          <div>
            {availablePlans.length === 0 ? (
              <div className="text-center py-4">
                <i className="ti ti-package-off fs-1 text-muted mb-3"></i>
                <h5 className="mb-2">No Available Plans</h5>
                <p className="text-muted">
                  {user.userFullName} is already enrolled in all available subscription plans.
                </p>
                <button
                  className="btn btn-secondary"
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                <div className="mb-4">
                  <label className="form-label fw-medium">Select Subscription Plan</label>
                  <CommonSelectV2
                    options={planOptions}
                    onChange={handlePlanChange}
                    placeHolder="Choose a subscription plan..."
                    disabled={isLoading}
                  />
                </div>

                {selectedPlan && (
                  <div className="card border-primary mb-4">
                    <div className="card-header bg-light-primary">
                      <h6 className="mb-0">Selected Plan Details</h6>
                    </div>
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label small text-dark">Plan Name</label>
                            <p className="fw-medium mb-0">{selectedPlan.subscriptionPlanName}</p>
                          </div>
                          <div className="mb-3">
                            <label className="form-label small text-dark">Plan Type</label>
                            <div>
                              <span className={`badge ${getPlanTypeBadgeClass(selectedPlan.planType)}`}>
                                {selectedPlan.planType}
                              </span>
                            </div>
                          </div>
                          <div className="mb-3">
                            <label className="form-label small text-dark">Duration</label>
                            <p className="mb-0">
                              {selectedPlan.duration ? `${selectedPlan.duration} months` : 'Lifetime'}
                            </p>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className="mb-3">
                            <label className="form-label small text-dark">Price</label>
                            <p className="fw-medium mb-0">
                              {selectedPlan?.currencyCode} {selectedPlan?.pricingInfo?.price?.toFixed(2)}
                            </p>
                            {selectedPlan?.pricingInfo?.promotionalPrice > 0 && (
                              <p className="text-muted small mb-0">
                                <del>{selectedPlan?.pricingInfo?.currencyCode} {selectedPlan?.pricingInfo?.promotionalPrice?.toFixed(2)}</del>
                              </p>
                            )}
                          </div>
                          <div className="mb-3">
                            <label className="form-label small text-dark">Description</label>
                            <p className="mb-0 small">{selectedPlan.subscriptionDescription || 'No description available'}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div className="d-flex justify-content-end gap-2">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={onClose}
                    disabled={isLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={isLoading || !selectedPlan}
                  >
                    {isLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Enrolling...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-plus me-1"></i>
                        Enroll User
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        }
      />
    </>
  );
};

export default AddSubscriptionModal;
